import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Trash2, Check, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PROPERTIES = {
  "de-la-vie": "De La Vie Maison",
  heritage: "Heritage",
  hearthstead: "Hearthstead Cottage",
};

const fmt = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" });

const dayAfter = (iso) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + 1);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

export default function NudgeList({ passcode, refreshKey }) {
  const [stays, setStays] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const headers = { "X-Upload-Passcode": passcode };

  useEffect(() => {
    if (!passcode) return;
    axios.get(`${API}/stays`, { headers }).then((r) => setStays(r.data)).catch(() => setStays([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passcode, refreshKey]);

  const sendNow = async (id) => {
    setBusyId(id);
    try {
      const r = await axios.post(`${API}/stays/${id}/send`, null, { headers });
      setStays((s) => s.map((x) => (x.id === id ? r.data : x)));
      toast.success("Review invite sent to the guest.");
    } catch {
      toast.error("Could not send the invite. Try again in a moment.");
    } finally {
      setBusyId(null);
    }
  };

  const cancel = async (id) => {
    try {
      await axios.delete(`${API}/stays/${id}`, { headers });
      setStays((s) => s.filter((x) => x.id !== id));
      toast.success("Removed.");
    } catch {
      toast.error("Could not remove — check your passcode.");
    }
  };

  if (stays.length === 0) return null;

  return (
    <div data-testid="nudge-list" className="mt-8 border-t border-[#f7f2e7]/10 pt-7">
      <p className="text-[10px] uppercase tracking-[0.25em] text-[#f7f2e7]/60">Automatic review invites</p>
      <p className="text-xs text-[#f7f2e7]/60 font-light mt-2 mb-5">
        Guests you added when blocking dates are emailed the morning after checkout.
      </p>
      <div className="space-y-4">
        {stays.map((s) => (
          <div key={s.id} data-testid={`nudge-item-${s.id}`} className="border-b border-[#f7f2e7]/10 pb-4 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm truncate">
                {s.guest_name} · {PROPERTIES[s.property] || s.property}
              </p>
              <p className="text-[10px] text-[#f7f2e7]/45 mt-1 truncate">
                {fmt(s.start)} – {fmt(s.end)} · {s.guest_email}
              </p>
              <p
                data-testid={`nudge-status-${s.id}`}
                className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] mt-2 ${
                  s.nudge_sent_at ? "text-[#c9a227]" : "text-[#f7f2e7]/70"
                }`}
              >
                {s.nudge_sent_at ? (
                  <><Check className="w-3 h-3" strokeWidth={2} /> Sent {new Date(s.nudge_sent_at).toLocaleDateString()}</>
                ) : (
                  <><Clock className="w-3 h-3" strokeWidth={1.5} /> Scheduled for {dayAfter(s.end)}</>
                )}
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {!s.nudge_sent_at && (
                <button
                  data-testid={`nudge-send-${s.id}`}
                  onClick={() => sendNow(s.id)}
                  disabled={busyId === s.id}
                  className="inline-flex items-center gap-1.5 border border-[#f7f2e7]/30 text-[#f7f2e7] text-[10px] uppercase tracking-[0.2em] px-3 py-2 hover:border-[#f7f2e7] disabled:opacity-60 transition-colors duration-300"
                >
                  <Send className="w-3.5 h-3.5" strokeWidth={1.5} /> Send now
                </button>
              )}
              <button
                data-testid={`nudge-cancel-${s.id}`}
                onClick={() => cancel(s.id)}
                aria-label="Remove"
                className="text-[#f7f2e7]/50 hover:text-[#c9a227] transition-colors duration-300"
              >
                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
